import { Record as ImRecord } from 'immutable';
import { partition, uniq } from 'lodash';
import core, { GQL, App, Viewport } from '@gin-betting/core';
import { MessageDescriptor } from 'react-intl';

import {
    BettingWidgetsOptions,
    SkipInitialRequestByPage,
    State,
    FullStateShape,
    TimeUnit,
    BettingSettingsOptions,
} from 'types';
import getExtraMatchListRoutes from 'utils/getExtraMatchListRoutes';
import globalMessages from './messages';

const {
    utils: { getBettingOptions },
} = core;

export enum EventType {
    Redirect = 'redirect',
    OpenBetsHistory = 'open-bets-history',
    HandleNotEnoughBalance = 'handle-not-enough-balance',
}

export enum Destination {
    Sport = 'sport',
    Esport = 'esport',
    MixSports = 'mix-sports',
    Login = 'login',
    Outright = 'outright',
    Live = 'live',
    Results = 'results',
}

export enum SidebarView {
    betslip = 'betslip',
    bets = 'bets',
    promo = 'promo',
    live = 'live',
}

export enum MatchMarketType {
    LiveMatchMarket = 'liveMatchMarket',
    FooterMarket = 'footerMarket',
}

export enum MatchListType {
    UPCOMING = 'upcoming',
    RESULTS = 'results',
    LIVE = 'live',
    PREMATCH = 'prematch',
}

export enum BettingWidgets {
    BETSLIP_SIDEBAR = 'betslipSidebar',
    CATEGORIZER = 'categorizer',
    SETTINGS = 'settings',
    MOBILE_HEADER = 'mobileHeader',
    STREAM = 'stream',
}

export enum LocalRouts {
    FAVORITE_UPCOMING = '/favorite-upcoming',
    FAVORITE_RESULTS = '/favorite-results',
    FAVORITE_OUTRIGHTS = '/favorite-outrights',
    SEARCH = '/search',
}

export const DEFAULT_WIDGET_OPTIONS: BettingWidgetsOptions = {
    [BettingWidgets.BETSLIP_SIDEBAR]: {},
    [BettingWidgets.CATEGORIZER]: {},
    [BettingWidgets.MOBILE_HEADER]: {
        containerElementId: 'header-betting-block',
    },
    [BettingWidgets.SETTINGS]: { containerElementId: '__app-oddFormat-select' },
    [BettingWidgets.STREAM]: {
        scrollToFirstStream: true,
        pinMinimizedStreamOutOfView: true,
        disableTheaterMode: false,
        disableMatchTracker: false,
        ignoreOffsetInTheaterMode: false,
    },
};

export const ROOT_STYLE_ID_ELEMENT = 'betting-style-root' as const;

export const DEFAULT_BREAKPOINTS: Viewport.BreakpointsSettings = {
    superExtraSmall: 360,
    extraSmall: 480,
    small: 768,
    medium: 1096,
    large: 1347,
    major: 1366,
    extraMajor: 1439,
    great: 1560,
    extraLarge: 1920,
};

export const supportedLocales = [
    'en',
    'ru',
    'zh',
    'sv',
    'pt',
    'ru_RF',
] as const;

export const marketGroupDivider = '~';

export const ownProviders: string[] = ['0', '4'];

export const springConfig = {
    stiffness: 150,
    damping: 20,
    precision: 0.1,
};

const stateShape: {
    [key in keyof FullStateShape]: undefined;
} = {
    entities: undefined,
    filters: undefined,
    betslip: undefined,
    viewport: undefined,
    ui: undefined,
    match: undefined,
    matchList: undefined,
    pagination: undefined,
    freebet: undefined,
    betsHistory: undefined,
    favorite: undefined,
    stream: undefined,
    banners: undefined,
    restrictions: undefined,
    favoriteMarkets: undefined,
    promo: undefined,
};

export const AppStateRecord = ImRecord<
    FullStateShape | Partial<FullStateShape>
>(stateShape, 'AppState');

export const MATCHES_LIMIT_PER_PAGE = 20;
export const OUTRIGHTS_LIMIT_PER_PAGE = 100;
export const POLLING_EDGE_PAGE = 2; // no polling below that pagination page

export const defaultStreamUrl = 'about:blank';

export const ExtendedRouts = {
    ...App.Routes,
    ...LocalRouts,
};

export const PublicRoutes = [
    ExtendedRouts.UPCOMING_LIST,
    ExtendedRouts.LIVE_LIST,
    ExtendedRouts.PREMATCH,
    ExtendedRouts.OUTRIGHTS_LIST,
    ExtendedRouts.RESULTS_LIST,
    ExtendedRouts.FAVORITE_UPCOMING,
    ExtendedRouts.FAVORITE_OUTRIGHTS,
    ExtendedRouts.FAVORITE_RESULTS,
    ExtendedRouts.CLASSIC,
    ExtendedRouts.SEARCH,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.LIVE_LIST}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.PREMATCH}`,
    ExtendedRouts.ESPORT,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.LIVE_LIST}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.PREMATCH}`,
];

export const SlugPublicRoutes = [
    `${ExtendedRouts.ESPORT}${ExtendedRouts.MATCH}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.MATCH}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.TOURNAMENT}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.TOURNAMENT}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.OUTRIGHT}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.OUTRIGHT}`,
    `${ExtendedRouts.ESPORT}${ExtendedRouts.RESULT_MATCH}`,
    `${ExtendedRouts.CLASSIC}${ExtendedRouts.RESULT_MATCH}`,
];

export const favoritePages = {
    upcoming: ([ExtendedRouts.FAVORITE_UPCOMING] as any) as App.Routes[],
    result: ([ExtendedRouts.FAVORITE_RESULTS] as any) as App.Routes[],
    outright: ([ExtendedRouts.FAVORITE_OUTRIGHTS] as any) as App.Routes[],
};

export const BASE_CURRENCY = 'EUR' as const;

export const BET_STEPS = {
    10: 1,
    50: 5,
    100: 10,
    500: 50,
    1000: 100,
    5000: 500,
    10000: 1000,
    50000: 5000,
    100000: 10000,
    Infinity: 50000,
} as const;

export const isMobile = checkMobile();

export const CLASS_NAME_OF_DISABLED_PAGE = '__app--is-page-disabled' as const;
export const CLASS_NAME_OF_DISABLED_SCROLL = '__app--is-scroll-disabled' as const;

/* eslint-disable */
function checkMobile() {
    let check = false;
    (a => {
        if (
            /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
                a
            ) ||
            /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i.test(
                a.substr(0, 4)
            )
        ) {
            check = true;
        }

        if (
            navigator.userAgent.match(
                /(ipad|tablet|(android(?!.*mobile))|(windows(?!.*phone)(.*touch))|kindle|playbook|silk|(puffin(?!.*(IP|AP|WP))))/i
            )
        ) {
            check = true;
        }
    })(navigator.userAgent || navigator.vendor || window.opera);

    return check;
}
/* eslint-enable */

export const DEFAULT_MAX_STAKE = 99999 as const;

// TODO: Move to core constant, use in "getBettingOptions" util
export const BETTING_OPTIONS_KEY = 'bettingOptions' as const;
export const DEBUG_INFO_LOCALSTORAGE_KEY = 'debug-info' as const;

export const VIEWABLE_SPORT_LIST = [
    'football',
    'basketball',
    'ice_hockey',
    'tennis',
    'american_football',
    'handball',
    'volleyball',
    'beach_volleyball',
    'table_tennis',
    'badminton',
    'rugby',
    'rugby_league',
    'baseball',
    'cricket',
    'darts',
    'futsal',
    'snooker',
] as const;

export enum MetaName {
    BetradarId = 'betradar_id',
    BetradarLMTCoverageLevel = 'betradar_lmt_coverage_level',
    Time = 'time',
    TimerRunning = 'timer_running',
    CountryCode = 'country_code',
    Logo = 'logo',
    Slug = 'slug',
    Organization = 'organization',
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const SELECTOR_STUB = (_state: State, _props?: object): null => null;

export const DEFAULT_STREAM_URL = 'about:blank' as const;

export const SKIP_MATCH_LIST_INITIAL_REQUEST: SkipInitialRequestByPage = {
    [App.Routes.PREMATCH]: {
        pages: [App.Routes.PREMATCH],
        filterKey: 'dateTo',
    },
    [App.Routes.LIVE_LIST]: {
        pages: [App.Routes.LIVE_LIST],
        filterKey: 'dateTo',
    },
};

export const matchListRoutes = getExtraMatchListRoutes(ExtendedRouts);

// TODO: [GINFE-1360] remove hardcode
export const [esportIds, classicIds] = partition(
    uniq(getBettingOptions().sportIds),
    sportId => sportId.includes('esports_')
);

export const initialSelectedSportIds = getBettingOptions().selectedSportIds;

export const AVAILABLE_ROUTES_WITH_PARENT_ROUTE = [
    App.Routes.UPCOMING_LIST,
    App.Routes.LIVE_LIST,
    App.Routes.PREMATCH,
];
export const enum TOP_MARKETS_TYPE {
    SINGLE = 'SINGLE',
    MULTIPLE = 'MULTIPLE',
}

export const TOP_MARKETS_TYPE_MAPPING = {
    // Esports
    esports_dota_2: TOP_MARKETS_TYPE.MULTIPLE,
    esports_fifa: TOP_MARKETS_TYPE.MULTIPLE,
    esports_basketball: TOP_MARKETS_TYPE.MULTIPLE,
    esports_ice_hockey: TOP_MARKETS_TYPE.MULTIPLE,
    esports_nba_2k18: TOP_MARKETS_TYPE.SINGLE,
    esports_league_of_legends: TOP_MARKETS_TYPE.MULTIPLE,
    esports_heroes_of_the_storm: TOP_MARKETS_TYPE.SINGLE,
    esports_street_fighter_5: TOP_MARKETS_TYPE.SINGLE,
    esports_artifact: TOP_MARKETS_TYPE.MULTIPLE,
    esports_hearthstone: TOP_MARKETS_TYPE.MULTIPLE,
    esports_counter_strike: TOP_MARKETS_TYPE.MULTIPLE,
    esports_tennis: TOP_MARKETS_TYPE.MULTIPLE,
    // Classic
    football: TOP_MARKETS_TYPE.MULTIPLE,
    basketball: TOP_MARKETS_TYPE.MULTIPLE,
    tennis: TOP_MARKETS_TYPE.MULTIPLE,
    ice_hockey: TOP_MARKETS_TYPE.MULTIPLE,
    volleyball: TOP_MARKETS_TYPE.MULTIPLE,
    baseball: TOP_MARKETS_TYPE.MULTIPLE,
    boxing: TOP_MARKETS_TYPE.MULTIPLE,
    futsal: TOP_MARKETS_TYPE.SINGLE,
    handball: TOP_MARKETS_TYPE.MULTIPLE,
    mma: TOP_MARKETS_TYPE.MULTIPLE,
    snooker: TOP_MARKETS_TYPE.SINGLE,
    american_football: TOP_MARKETS_TYPE.MULTIPLE,
};

export enum CMS_LINKS {
    STATISTIC = '/sport-statistics',
}

export const ACTIVE_MARKET_STATUSES = [
    GQL.MarketStatus.ACTIVE,
    GQL.MarketStatus.SUSPENDED,
];

export const LIVE_SPORTEVENT_STATUSES = [
    GQL.SportEventStatus.LIVE,
    GQL.SportEventStatus.SUSPENDED,
];

export const betTypesMessage: { [key in GQL.BetType]: MessageDescriptor } = {
    [GQL.BetType.SINGLE]: globalMessages.SINGLE,
    [GQL.BetType.EXPRESS]: globalMessages.EXPRESS,
    [GQL.BetType.SYSTEM]: globalMessages.SYSTEM,
};

export const TIME_UNIT_IN_MILLISECONDS: Record<TimeUnit, number> = {
    minute: 1000 * 60,
    hour: 1000 * 60 * 60,
    day: 1000 * 60 * 60 * 24,
};

export const TITLES_BY_SPECIFIER = {
    top: globalMessages.top,
    handicap: globalMessages.marketHandicap,
    total: globalMessages.marketTotal,
} as const;

export const THEME_KEY_LOCAL_STORAGE = 'theme' as const;
export const THEME_BODY_ATTR = 'data-betting-theme' as const;

export enum BettingSettingsKeys {
    THEMES = 'theme',
    ODD_FORMAT = 'oddFormat',
    CALLBACKS = 'callbacks',
}

export const DEFAULT_BETTING_SETTINGS: BettingSettingsOptions = {
    [BettingSettingsKeys.THEMES]:
        (window.localStorage.getItem(THEME_KEY_LOCAL_STORAGE) as Themes) ||
        'dark',
    [BettingSettingsKeys.ODD_FORMAT]: App.OddFormat.Decimal,
    [BettingSettingsKeys.CALLBACKS]: {},
};

export const countryReg = new RegExp(/\(([a-zA-Z]{2}-)?[a-zA-Z]{2,8}\)/, 'g');
